import { ApolloQueryResult, useQuery } from '@apollo/client';
import {
  ReservationMode,
  ReservationModeFilter,
  ReservationModePaginationResponse,
} from '../types';
import { useState } from 'react';
import { defaultPageInfo } from 'lib/enums/pageInfo';
import { PageInfo } from 'lib/types/pageInfo';
import { GET_MODES_PAGINATION } from 'pagesAdmin/SettingsPage/gqlSchemes';
import { useDebounce } from 'use-debounce';

type GetModesPaginationVariables = {
  filter: ReservationModeFilter;
  page: number;
  perPage: number;
};

const useGetModesAutocomplete = ({
  initialPage = 0,
  perPage = 10,
}: {
  initialPage?: number;
  perPage?: number;
} = {}) => {
  const [items, setItems] = useState<ReservationMode[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPageInfo);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [search, setSearch] = useState<string>(''); // State for search term
  const [debouncedSearch] = useDebounce(search, 300); // Debounced search term

  const { loading, fetchMore } = useQuery<
    {
      schedule: {
        reservationModePagination: ReservationModePaginationResponse;
      };
    },
    GetModesPaginationVariables
  >(GET_MODES_PAGINATION, {
    variables: {
      filter: { name: debouncedSearch },
      page: initialPage,
      perPage,
    },
    onCompleted: (queryData) => {
      if (queryData?.schedule.reservationModePagination) {
        setItems(queryData.schedule.reservationModePagination.items);
        setPageInfo(queryData.schedule.reservationModePagination.pageInfo);
      }
    },
  });

  const loadMore = async () => {
    if (pageInfo.hasNextPage) {
      const nextPage = currentPage + 1;
      const result: ApolloQueryResult<{
        schedule: {
          reservationModePagination: ReservationModePaginationResponse;
        };
      }> = await fetchMore({
        variables: {
          filter: { name: debouncedSearch },
          page: nextPage,
          perPage,
        },
      });

      if (result.data.schedule.reservationModePagination) {
        setItems((prevItems) => [
          ...prevItems,
          ...result.data.schedule.reservationModePagination.items,
        ]);
        setPageInfo(result.data.schedule.reservationModePagination.pageInfo);
        setCurrentPage(nextPage);
      }
    }
  };

  return {
    modes: items,
    pageInfo,
    loading,
    setSearch,
    loadMore,
  };
};

export default useGetModesAutocomplete;
